<script lang="ts">
/**
 * Grid of metric cards for dashboard-style stat displays.
 *
 * Renders a 3-column grid of stat cards with icon, value, and label.
 * On desktop, cards are bordered and have a subtle background.
 *
 * @example
 * ```vue
 * <StatCards :stats="[
 *   { title: 'Items', icon: ICON.item, value: 142 },
 *   { title: 'Containers', icon: ICON.container, value: 18 },
 *   { title: 'To Catalog', icon: ICON.toCatalog, value: 23 },
 * ]" />
 * ```
 */
export interface StatCard {
  /** Label text below the value. */
  title: string
  /** Icon identifier rendered via `UIcon`. */
  icon: string
  /** The metric value to display. */
  value: number
  /** Optional badge text below the label (e.g. "3 today"). */
  subtitle?: string
}

export interface StatCardsProps {
  /** Array of stat card definitions. */
  stats: StatCard[]
}
</script>

<script setup lang="ts">
defineProps<StatCardsProps>()
</script>

<template>
  <div
    class="lg:border-default grid grid-cols-3 gap-1 lg:gap-px lg:overflow-hidden lg:rounded-lg lg:border"
  >
    <div
      v-for="(stat, index) in stats"
      :key="index"
      class="lg:bg-elevated/25 flex flex-col items-center gap-1 rounded-lg p-4 lg:rounded-none lg:p-6"
    >
      <div class="flex items-center gap-1">
        <UIcon :name="stat.icon" class="text-primary size-4 lg:size-5" />
        <span class="text-highlighted text-xl font-semibold tabular-nums lg:text-2xl">
          {{ stat.value.toLocaleString() }}
        </span>
      </div>
      <span class="text-muted text-xs uppercase">{{ stat.title }}</span>
      <UBadge v-if="stat.subtitle" color="success" variant="subtle" class="mt-1 text-xs">
        {{ stat.subtitle }}
      </UBadge>
    </div>
  </div>
</template>
